import { useCallback, useMemo } from "react";
import { Reservation } from "../types/Reservation";
import { hayConflicto } from "./useReservaValidacion";

/**
 * Hook para detectar reservas superpuestas y manejar el estado visual
 */
export const useOverlapDetection = (reservations: Reservation[]) => {
  // Detectar todas las reservas que tienen superposición
  const overlappingReservations = useMemo(() => {
    const overlapping: number[] = [];

    reservations.forEach((reservation) => {
      const hasConflict = hayConflicto(
        reservation,
        reservations,
        reservation.id
      );
      if (hasConflict) {
        overlapping.push(reservation.id);
      }
    });

    return overlapping;
  }, [reservations]);

  const overlappingReservationIds = useMemo(
    () => new Set(overlappingReservations),
    [overlappingReservations]
  );

  // Función para verificar si una reserva específica está superpuesta
  const isReservationOverlapping = useCallback(
    (reservationId: number): boolean =>
      overlappingReservationIds.has(reservationId),
    [overlappingReservationIds]
  );

  // Función para obtener todas las reservas superpuestas
  const getOverlappingReservations = useCallback(
    (): Reservation[] =>
      reservations.filter((reservation) =>
        overlappingReservationIds.has(reservation.id)
      ),
    [reservations, overlappingReservationIds]
  );

  return {
    overlappingReservations,
    isReservationOverlapping,
    getOverlappingReservations,
    hasOverlaps: overlappingReservations.length > 0,
  };
};
